import { formatCurrency, formatDate, formatNumber, formatPercent } from "@angular/common";
import { Injectable, LOCALE_ID, inject } from "@angular/core";
import { AbstractControl, FormGroup } from "@angular/forms";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { BrowserFontSize, BrowserService, CachedRepositoryService } from "@tylertech/core-module";
import { ODataFilterFactoryService } from "@tylertech/odata-module";
import { Utilities } from "@tylertech/shared-module";
import { ClassMetadata, MetadataVerification, PropertyMetadata, } from "@tylertech/tyler-api-types";
import { EntityWrapper, IAugmentedWithParent, LazyLoadedProxyHandler, MetadataIntermediary, ModelChangeArg, RepositoryFormData, } from "../interfaces";
import { MetadataEventService } from "./metadataEventService";
import { GridEventService, MetadataTableEventArg } from "./table/gridEvent.service";
import { MultipleEntityEditService } from "./multipleEntityEditService";
import { getLoggerFor, LogInvocation, LogLevel } from "@tylertech/logging";
import { Observable, Subject } from "rxjs";

@Injectable({ providedIn: "root" })
export class MetadataService
{
    private readonly locale = inject(LOCALE_ID);
    private readonly $commonDialogsService = inject(CommonDialogsService);
    private readonly $metadataEventService = inject(MetadataEventService);
    private readonly $gridEventService = inject(GridEventService);
    private readonly $odataFilterFactory = inject(ODataFilterFactoryService);
    private readonly $repositoryService = inject(CachedRepositoryService);
    private readonly $multipleEditEntityService = inject(MultipleEntityEditService);
    private readonly $browserService = inject(BrowserService);

    private readonly log = getLoggerFor("MetadataService");

    AugmentModels(metadata: ClassMetadata, entities: any[], selectProperty: string): Promise<void>
    {
        const loaders: Promise<void>[] = [];
        for (let i = 0; i < entities.length; i++)
        {
            const entity: IAugmentedWithParent = entities[i];
            const index = i;
            if (!entity["$isEntityWrapper"])
            {
                loaders.push(
                    this.AugmentModel(metadata.TypeName, entity, selectProperty)
                        .then(r =>
                        {
                            entities[index] = r;
                        })
                );
            }
        }
        return Promise.all(loaders)
            .then(() =>
            {
                /* Squashing Array<void>... */
            });
    }

    AugmentModel(typeName: string, model: any, selectProperty?: string): Promise<any>
    {
        // Respect the type name from backend (e.g. implementation type vs interface used in metadata)
        if (!model.$$TypeName)
        {
            model.$$TypeName = typeName;
        }
        model["$$SelectProperty"] = selectProperty;
        let modelProxy = model;
        if (!modelProxy["$isEntityWrapper"])
        {
            const modelWrapper = new EntityWrapper(model, this);
            modelProxy = new Proxy(modelWrapper, new LazyLoadedProxyHandler(this));
        }
        return Promise.resolve(modelProxy);
    }

    VerifyLoadChildren(properties: PropertyMetadata[], typeName: string, model: any, selectProperty?: string): Promise<any>
    {
        return this.AugmentModel(typeName, model, selectProperty)
            .then(modelProxy =>
            {
                return this.VerifyLoadChildrenImpl(properties, modelProxy)
                    .then(_v => modelProxy);
            });
    }

    /**
     * Recursively verifies and loads child properties for a given model object based on provided property metadata.
     *
     * This method traverses the specified properties, checking if each property with associated class metadata
     * is loaded in the model. If a property is not loaded or requires fetching, it initiates asynchronous loading
     * (potentially via a fetch operation) and proxies the result. For properties that are already loaded and are
     * objects (not arrays), it recursively descends into their children. For properties that require fetching,
     * it constructs a filter and fetches the data, then proxies and attaches the result to the model.
     *
     * @protected
     * @param properties - An array of `PropertyMetadata` describing the properties to verify and load.
     * @param model - The proxied model object whose children are to be verified and loaded.
     * @returns A promise that resolves to the updated model object with all required children loaded.
     */
    @LogInvocation(LogLevel.DEBUG)
    protected VerifyLoadChildrenImpl(properties: PropertyMetadata[], model: EntityWrapper): Promise<any>
    {
        const self = this;
        const promises: Promise<any>[] = [];
        properties//.filter(p => p.ClassMetadata && !this.IsHiddenProperty(p))
            .forEach(property =>
            {
                if (!property.ClassMetadata || this.IsHiddenProperty(property))
                {
                    return;
                }

                if (model[property.Name] && Object.keys(model[property.Name]).length > 0)
                {
                    // do we need to descend into children?
                    if (!Array.isArray(model[property.Name]))
                    {
                        promises.push(
                            this.VerifyLoadChildren(property.ClassMetadata.Properties, property.ClassMetadata.TypeName, model[property.Name])
                                .then(r =>
                                {
                                    model[property.Name] = r;
                                    return r;
                                })
                        );
                    }
                }
                else
                {
                    const subModel = getSubPromise(model, property);
                    if (subModel)
                    {
                        promises.push(
                            subModel.then(s =>
                            {
                                if (Array.isArray(s))
                                {
                                    return Promise.all(
                                        s.map(sub => self.VerifyLoadChildrenImpl(property.ClassMetadata.Properties, sub))
                                    );
                                }
                                else return self.VerifyLoadChildrenImpl(property.ClassMetadata.Properties, s);
                            })
                        );
                    }
                }
            });

        return Promise.all(promises)
            .then(_v => model);

        function getSubPromise(model: EntityWrapper, property: PropertyMetadata): Promise<any> | undefined
        {
            if (!self.propertyNeedsFetching(model, property))
            {
                return;
            }

            const filter = self.CreateLoadFilter(
                model,
                property.ToColumnId,
                property.FromColumnId,
                property.ConstantFilter);

            if (!filter)
            {
                // set an empty child that to use
                const wrapper = new EntityWrapper({}, self);
                wrapper.wasCreated = true;
                model.SetLazyLoadParent(property, new Proxy(wrapper, new LazyLoadedProxyHandler(self)));
                return Promise.resolve(wrapper);
            }

            const r = self.Fetch(property, filter)
                .then(x =>
                {
                    if (Array.isArray(x))
                    {
                        // REVIEW do we need to do it on child entities does not seem like it we should not change our primary key
                        model[property.Name] = x.map(xx =>
                        {
                            const wrapper = new EntityWrapper(xx, self);
                            return new Proxy(wrapper, new LazyLoadedProxyHandler(self));
                        });
                    }
                    else if (!property.ClassMetadata || !property.ClassMetadata.IsEnumerable)
                    {
                        const wrapper = new EntityWrapper(x ?? {}, self);
                        if (!x) wrapper.wasCreated = true;
                        model.SetLazyLoadParent(property, new Proxy(wrapper, new LazyLoadedProxyHandler(self)));
                    }

                    return model[property.Name];
                });
            return r;
        }
    }

    @LogInvocation(LogLevel.DEBUG, true)
    private propertyNeedsFetching(model: EntityWrapper, property: PropertyMetadata): boolean
    {
        return Utilities.IsNullOrUndefinedOrEmptyObject(model[property.Name])
            && !!property.FromColumnId
            && !!property.ToColumnId;
    }

    Fetch(property: PropertyMetadata, filter: string): Promise<any | Array<any>>
    {
        return this.$repositoryService.Fetch(property.ClassMetadata.TypeName, filter)
            .then(x =>
            {
                return property.ClassMetadata.IsEnumerable !== true
                    ? x[0]
                    : x;
            });
    }

    @LogInvocation(LogLevel.DEBUG, true)
    CreateLoadFilter(model: any, to: string, from: string, constantFilter?: string): string
    {
        if (Utilities.IsNullOrUndefinedOrEmpty(constantFilter) && (!to || !from)) return null;
        const builder = this.$odataFilterFactory.createBuilder();
        if (to && from)
        {
            const tos = to.split(",");
            const froms = from.split(",");
            for (let index = 0; index < tos.length; index++)
            {
                const v = this.DependentModuleValue(froms[index], null, model);
                if (Utilities.IsNullOrUndefined(v) || v === Utilities.emptyGuid) continue;
                builder.filterEQ(tos[index], v);
            }
        }
        if (!Utilities.IsNullOrUndefinedOrEmpty(constantFilter))
        {
            constantFilter = this.EvaluateString(constantFilter, null, model, null, true);
            const filter = builder.build()?.replace("&$filter=", "");
            if (filter !== constantFilter)
                builder.constantFilter(`${constantFilter}`);
        }
        return builder.build();
    }

    IsHiddenProperty(metadataProperty: PropertyMetadata): boolean
    {
        if (metadataProperty.Hidden) return true;
        return false;
    }

    /**
     * Flattens the properties of a given class metadata into an array of `MetadataIntermediary` objects,
     * traversing nested class metadata recursively. Supports filtering by specific property paths and
     * tracks parent property names and nesting levels.
     *
     * @param metadata - The `ClassMetadata` object describing the structure to flatten.
     * @param parent - The parent object in the hierarchy, used for reference in the intermediary.
     * @param model - The model instance corresponding to the metadata, used for value extraction and proxying.
     * @param specificProperties - Optional array of property paths (dot-separated) to include; if null, includes all.
     * @param parentPropertyName - Dot-separated path of the parent property, used for nested property tracking.
     * @param level - The current recursion depth, used for property path resolution.
     * @returns An array of `MetadataIntermediary` objects representing the flattened properties.
     */
    FlattenProperties(metadata: ClassMetadata, parent: any, model: any, filteredColumnsMap: Map<number, Set<string>> = null, parentPropertyName: string = "", level: number = 0): MetadataIntermediary[]
    {
        const self = this;
        if (Utilities.IsNullOrUndefined(metadata))
        {
            return [];
        }

        if (parent && !model["$$Parent"])
        {
            model["$$Parent"] = parent;
        }

        model["$$TypeName"] = metadata.TypeName;

        const ret = [];
        metadata.Properties
            // .filter(f => )
            .forEach(property =>
            {
                if (!propertyMatch(property.Name, level))
                {
                    return;
                }

                self.log.debug(`FlattenProperties | visiting "${property.Name}"`, { property });
                if (property.ClassMetadata && !this.IsHiddenProperty(property))
                {
                    const subModel = getSubModel(model, property.Name, property);
                    if (subModel)
                    {
                        let parentNamePath = property.Name;
                        if (parentPropertyName !== "")
                        {
                            parentNamePath = `${parentPropertyName}.${parentNamePath}`;
                        }

                        if (Array.isArray(subModel))
                        {
                            const subModelRet = subModel
                                .map(sub => this.FlattenProperties(property.ClassMetadata, model, sub, filteredColumnsMap, parentNamePath, level + 1))
                                .reduce((p, c) => p.concat(c), []);
                            ret.push(...subModelRet);
                        }
                        else
                        {
                            ret.push(
                                ...this.FlattenProperties(property.ClassMetadata, model, subModel, filteredColumnsMap, parentNamePath, level + 1));
                        }

                        if (property.InputType === "Label" || property.InputType === "Header")
                        {
                            ret.unshift(createMetadataIntermediary(property, metadata, property.Name, parent, model));
                        }
                    }
                }
                else
                {
                    if (!Utilities.IsNullOrUndefinedOrEmpty(parentPropertyName))
                    {
                        property["$parentPropertyName"] = parentPropertyName;
                    }
                    ret.push(createMetadataIntermediary(property, metadata, property.Name, parent, model));
                }
                return ret;
            });

        return ret;

        function getSubModel(model: any, field: string, property: PropertyMetadata): any
        {
            self.log.debug(`FlattenProperties->getSubModel(model, field, property)`, { args: { model, field, property } });
            if (Utilities.IsNullOrUndefined(model[field]) && !property.ClassMetadata.IsEnumerable)
            {
                const wrapper = new EntityWrapper(model[field] ?? {}, self);
                if (!model[field]) wrapper.wasCreated = true;
                model[field] = new Proxy(wrapper, new LazyLoadedProxyHandler(self));
            }

            const result = model[field];
            self.log.debug(`FlattenProperties->getSubModel(model, field, property) --> ${result}`, { args: { model, field, property }, result });
            return result;
        }

        function createMetadataIntermediary(
            propertyMetadata: PropertyMetadata,
            classMetadata: ClassMetadata,
            name: string,
            parent: any,
            model: any
        ): MetadataIntermediary
        {
            self.log.debug(`FlattenProperties->createMetadataIntermediary(propertyMetadata,classMetadata,name,parent,model)`, { args: { propertyMetadata, classMetadata, name, parent, model } });
            return {
                name: name,
                metadata: classMetadata,
                parent: parent,
                model: model,
                property: propertyMetadata,
            } satisfies MetadataIntermediary;
        }

        function propertyMatch(propertyName: string, level: number): boolean
        {
            if (!filteredColumnsMap || filteredColumnsMap.size === 0)
                return true;
            const levelSet = filteredColumnsMap.get(level);
            return levelSet ? levelSet.has(propertyName) : false;
        }
    }

    FireGridReload(type: string | undefined, all: boolean, propertiesToRecalc?: string[], reloadColumns?: boolean, clearFilter?: boolean, applyFilter?: boolean): void
    {
        this.$gridEventService.fireLoadMetadataTableListeners({
            Type: type,
            AllSubPropertyGrids: all,
            PropertiesToRecalc: propertiesToRecalc,
            ReloadColumns: reloadColumns,
            ClearFilter: clearFilter,
            ApplyFilter: applyFilter,
        });
        this.$metadataEventService.fireLoadCardMetadataListeners();
        this.$multipleEditEntityService.maybeEditNext();
        this.$multipleEditEntityService.maybeAddNew();
    }

    FireGridRefetch(type: any): void
    {
        // This will try to force projection grids to refetch from server
        // This will assume all cache for the target type has been cleared e.g. using AfterSaveContext.LookupsToRefresh
        this.$gridEventService.fireLoadMetadataTableListeners(<MetadataTableEventArg>{
            Type: type,
            AllSubPropertyGrids: true,
            Refetch: true,
        });
    }

    SetModuleValue(dependentProp: string, parent: any, model: any, newValue: any): void
    {
        const values = dependentProp.split(".");
        let current = model;
        const localParent = !Utilities.IsNullOrUndefined(parent) ? parent : model["$$Parent"];
        if (Utilities.isEmptyGuid(newValue)) newValue = null;
        for (let index = 0; index < values.length - 1; index++)
        {
            const element = values[index];
            if (current)
            {
                if (element === "Parent" && !Utilities.IsNullOrUndefined(localParent))
                {
                    current = localParent;
                }
                else
                {
                    current = current[element];
                }
            }
        }
        if (current)
            if (current)
            {
                // If the value is an array we don't want to replace the array because
                // anything that references the old array will not be updated.
                if (Array.isArray(current[values[values.length - 1]]))
                {
                    Utilities.mergeArray(current[values[values.length - 1]], newValue, current);
                }
                else if (
                    typeof current[values[values.length - 1]] === "object" &&
                    current[values[values.length - 1]] !== null &&
                    !(current[values[values.length - 1]] instanceof Date) &&
                    newValue != null
                )
                {
                    Utilities.merge(current[values[values.length - 1]], newValue);
                }
                else
                {
                    const oldValue = current[values[values.length - 1]];
                    current[values[values.length - 1]] = newValue;
                    if (oldValue !== newValue)
                    {
                        this.fireProxyPropertyChangedListeners({
                            Model: current,
                            Property: values[values.length - 1],
                            OldValue: oldValue,
                            NewValue: newValue
                        });
                    }
                }
            }
    }

    /**
     * evaluates propertyPaths against given model.
     * @param dependentProp
     * @param parent
     * @param model
     */
    @LogInvocation(LogLevel.DEBUG, true)
    DependentModuleValue(dependentProp: string, parent: any, model: any, formData?: RepositoryFormData): any
    {
        if (formData)
        {
            const pattern = /^OtherEntities\.([\w.]*)(?:\[(\d+)\])?\.?([\w.]*)?/;
            const matches = pattern.exec(dependentProp);
            if (!Utilities.IsNullOrUndefined(matches))
            {
                this.log.debug(`DependentModuleValue | searching formData`);
                const type = matches[1];
                const index = matches[2];
                const child = matches[3];
                if (!Utilities.IsNullOrUndefinedOrEmptyObject(formData.OtherEntities))
                {
                    return index
                        ? child
                            ? formData.OtherEntities[type][index][child]
                            : formData.OtherEntities[type][index]
                        : formData.OtherEntities[type];
                }
            }
        }

        this.log.debug(`DependentModuleValue | searching model`);
        dependentProp = dependentProp.split("|")[0];
        const values = dependentProp.split(".");
        let current = model;
        let currentParent = parent;
        values.forEach(element =>
        {
            if (current)
            {
                if (element === "Parent" && !Utilities.IsNullOrUndefined(parent))
                {
                    current = currentParent;
                    currentParent = current["$$Parent"];
                }
                else
                {
                    if (dependentProp.startsWith("Parent") && !Utilities.IsNullOrUndefined(current.parent))
                        current = current.parent[element]; //works
                    else current = current[element]; // this is the old code, it seems like there is an extra layer between parent and the values now
                }
            }
        });
        return current;
    }

    /**
     * Evaluates the given metadata value against the given parent/model pair and returns the value
     * of the described propertyPath; if not a property path the metadata value is returned.
     * @param metadataValue metadata value describing desired value
     * @param parent object that the model belongs to.
     * @param model model the that metadata is describing.
     */
    EvaluateString(metadataValue: string, parent: any, model: any, formData?: RepositoryFormData, urlEncodeValue?: boolean): string
    {
        let regexMatch = metadataValue.match("{[^}]*}");
        while (regexMatch !== null)
        {
            const moduleValue = this.ReplaceMatch(regexMatch, parent, model, formData, urlEncodeValue, metadataValue);
            metadataValue = metadataValue.replace(/{[^}]*}/, moduleValue);
            regexMatch = metadataValue.match("{[^}]*}");
        }
        return metadataValue;
    }

    ReplaceMatch(regexMatch: RegExpMatchArray, parent: any, model: any, formData: RepositoryFormData, urlEncodeValue: boolean, metadataValue: string): string
    {
        let moduleValue = this.DependentModuleValue(
            regexMatch[0].substring(1, regexMatch[0].length - 1),
            parent,
            model,
            formData
        );
        if (moduleValue === null || moduleValue === undefined)
        {
            moduleValue = "";
        }
        if (Array.isArray(moduleValue)) moduleValue = moduleValue.join(", ");
        // if the match length is the whole string then it is a constant filter property so do not url encode
        if ((typeof moduleValue === 'string' || moduleValue instanceof String) && urlEncodeValue && regexMatch[0].length !== metadataValue.length)
        {
            moduleValue = moduleValue
                .replace(/#/g, "%23")
                .replace(/\+/g, "%2B");
            if (!moduleValue.startsWith("'") && !moduleValue.endsWith("'"))
                moduleValue = moduleValue.replace(/'/g, "''");
            moduleValue = encodeURIComponent(moduleValue);
        }
        return moduleValue;
    }

    /**
     * Evaluates the given string as a logical set of propertyPaths against the given model/parent pair and returns the boolean value.
     * Supports logical OR conjunctives (||).
     * Supports logical AND conjunctives (&&)
     * Needs to stay in sync with MetadataGenerator.cs the method named EvaluateBoolean
     * @param prop metadata value describing the logical expression
     * @param parent object that the model belongs to
     * @param model model the that metadata is describing.
     */
    EvaluateBoolean(prop: string, parent: any, model: any, formData?: RepositoryFormData): boolean
    {
        if (Utilities.IsNullOrUndefined(prop))
        {
            return false;
        }
        // A || B && C      means A || (B && C)
        // A && B || C && D means (A && B) || (C && D)
        // A && B && C || D means ((A && B) && C) || D
        // !A && B || C     means ((!A) && B) || C
        prop = prop.trim();
        const orClauses = prop.split("||");
        if (orClauses.length > 1)
        {
            //this allows things like returning something different if a string is empty else the nonempty string (if that ever becomes necessary)
            let lastResult: any = undefined;
            for (let i = 0; i < orClauses.length; i++)
            {
                const result = this.EvaluateBoolean(orClauses[i], parent, model, formData);
                lastResult = result;
                if (result) return result;
            }
            return lastResult;
        }
        const andClauses = prop.split("&&");
        if (andClauses.length > 1)
        {
            let lastResult: any = undefined;
            for (let i = 0; i < andClauses.length; i++)
            {
                const result = this.EvaluateBoolean(andClauses[i], parent, model, formData);
                lastResult = result;
                if (!result) return result;
            }
            return lastResult;
        }
        if (prop.startsWith("!"))
        {
            prop = prop.substring(1);
            return !this.EvaluateBoolean(prop, parent, model, formData);
        }
        return this.DependentModuleValue(prop, parent, model, formData);
    }

    /**
     * Returns the CanAdd, CanEdit, CanDelete result for GridMetadata based on evaluation of the setting and target property.
     * @param bool CanAdd, CanEdit, CanDelete value from metadata
     * @param boolProp Target property for evaluating
     * @param model model the that metadata is describing.
     */
    GetPermission(bool: boolean, boolProp: string, model: any): boolean
    {
        if (Utilities.IsNullOrUndefined(boolProp)) return bool;
        if (!bool) return bool;
        const parent = model["$$Parent"];
        const evalBool = this.EvaluateBoolean(boolProp, parent, model);
        if (Utilities.IsNullOrUndefined(evalBool))
        {
            return bool;
        }
        return evalBool;
    }

    GetPermissionDenied(bool: boolean, boolProp: string, model: any, formData?: RepositoryFormData): boolean
    {
        if (Utilities.IsNullOrUndefined(boolProp)) return bool;
        if (bool) return bool;
        const parent = model["$$Parent"];
        const evalBool = this.EvaluateBoolean(boolProp, parent, model, formData);
        if (Utilities.IsNullOrUndefined(evalBool))
        {
            return bool;
        }
        return evalBool;
    }

    getFormattedValue(prop: MetadataIntermediary): string
    {
        if (
            prop.property.InputType === "Label" ||
            prop.property.InputType === "Header" ||
            prop.property.InputType === "Button"
        )
            return this.EvaluateString(prop.property.Label, prop.parent, prop.model);
        const value = prop.model[prop.name];
        return this.getFormattedValueFromProp(value, prop.property);
    }

    getFormattedValueFromProp(value: any, prop: PropertyMetadata): string
    {
        if (Utilities.IsNullOrUndefined(value)) return "";
        if (Utilities.IsNullOrUndefined(prop)) return value.toString();
        if (prop.InputType === "Date") return formatDate(value, "M/d/yyyy", this.locale);
        if (prop.InputType === "Currency") return formatCurrency(value, this.locale, "$");
        if (prop.InputType === "PhoneNumber") return this.formatPhone(value);
        if (!Utilities.IsNullOrUndefined(prop.Scale))
        {
            if (prop.InputType === "Percent")
                return formatPercent(value / 100, this.locale, this.digitsInfo(prop, value));
            return `${formatNumber(value, this.locale, this.digitsInfo(prop, value))}`;
        }
        return value.toString();
    }

    digitsInfo(propertyMetadata: PropertyMetadata, value: any): string
    {
        if (propertyMetadata.Scale)
            return `1.${propertyMetadata.Scale}-${propertyMetadata.Scale}`;
        const intScale = value.toString().includes(".") ? value.toString().split(".")[1].length : 0;
        return `1.${intScale}-${intScale}`;
    }

    ShowValidationDialog(metadataVerification: MetadataVerification): Promise<any>
    {
        type keyValue = {
            key: string,
            value: string[];
        };
        const keyedErrors = Object.keys(metadataVerification.FieldErrors)
            .map(k =>
            {
                return <keyValue>{
                    key: k,
                    value: metadataVerification.FieldErrors[k],
                };
            });
        const errors = keyedErrors.map(x => `${x.key}: ${x.value}`);
        return this.showDialog(errors);
    }

    private showDialog(errors: string[]): Promise<any>
    {
        const text = ["<p>Entity failed with following verification errors:</p><ul>"]
            .concat(errors.map(error => `<li>${error}</li>`))
            .concat("</ul>")
            .join("");

        return this.$commonDialogsService.showAlertMessage(
            text, "Verification Errors")
            .catch(_r =>
            {
                /* Squashing errors */
            });
    }

    formatPhone(str: string): string
    {
        const input = str.replace(/\D/g, "")
            .substring(0, str.length);
        const areaCode = input.substring(0, 3);
        const middle = input.substring(3, 6);
        const last = input.substring(6, 10);
        const ext = input.length > 10 ? input.substring(10, 20) : "";
        if (input.length > 10)
        {
            str = `(${areaCode}) ${middle}-${last} x${ext}`;
        }
        else if (input.length > 6)
        {
            str = `(${areaCode}) ${middle}-${last}`;
        }
        else if (input.length > 3)
        {
            str = `(${areaCode}) ${middle}`;
        }
        else if (input.length > 0)
        {
            str = `(${areaCode}`;
        }
        return str;
    }

    touchForm(controls: { [key: string]: AbstractControl<any, any>; }): void
    {
        Object.keys(controls)
            .forEach((control,) =>
            {
                if (controls[control] instanceof FormGroup)
                {
                    this.touchForm((controls[control] as FormGroup).controls);
                }
                else
                    controls[control].markAsTouched();
            });
    }

    invalidInput(error: string, control: AbstractControl<any>): boolean
    {
        return !Utilities.IsNullOrUndefinedOrEmpty(error) && (control?.touched);
    }

    getInputDensity(compact = false): string
    {
        if (compact)
        {
            return "extra-small";
        }

        const browserFontSize = this.$browserService.browserFontSetting;
        if (browserFontSize > BrowserFontSize.Medium && browserFontSize < BrowserFontSize.VeryLarge)
        {
            return "large";
        }
        else if (browserFontSize >= BrowserFontSize.VeryLarge)
        {
            return "extra-large";
        }

        return "small";
    }

    private readonly proxyPropertyChanged$ = new Subject<ModelChangeArg>();

    get onProxyPropertyChanged(): Observable<ModelChangeArg>
    {
        return this.proxyPropertyChanged$.asObservable();
    }

    fireProxyPropertyChangedListeners(arg: ModelChangeArg): void
    {
        this.proxyPropertyChanged$.next(arg);
    }

    compareModels(argModel: any, model: any): boolean
    {
        if (model["$isEntityWrapper"])
            return argModel === model.$value;
        return argModel === model;
    }
}
